/**
 * Navigation Constants for AI Agent Web Navigation
 *
 * Defines navigable categories, actions, and route mappings for the AI agent
 * to navigate users within the web application.
 */

import { Capabilities, type CapabilityAlias } from './capabilities';
import { RbacActions } from './rbac-actions';

/**
 * Navigation categories - high-level groupings of navigable areas
 */
export enum NavigationCategory {
	DASHBOARD = 'dashboard',
	CLIENTS = 'clients',
	CONTACTS = 'contacts',
	ORDERS = 'orders',
	QUOTES = 'quotes',
	INVOICES = 'invoices',
	PAYMENTS = 'payments',
	ITEMS = 'items',
	VENDORS = 'vendors',
	COMPETITORS = 'competitors',
	TASKS = 'tasks',
	ISSUES = 'issues',
	PURCHASE_ORDERS = 'purchase_orders',
	SOURCING = 'sourcing',
	PRICING = 'pricing',
	OPPORTUNITIES = 'opportunities',
	TRADE_SHOWS = 'trade_shows',
	JOBS = 'jobs',
	CAMPAIGNS = 'campaigns',
	COMMUNICATIONS = 'communications',
	DISTRIBUTION = 'distribution',
	WAREHOUSES = 'warehouses',
	SALES_ROUTES = 'sales_routes',
	COMPLIANCE = 'compliance',
	AUTOMATION = 'automation',
	AI_AGENTS = 'ai_agents',
	REPORTS = 'reports',
	SETTINGS = 'settings',
}

/**
 * Navigation actions - specific navigable destinations
 */
export enum NavigationAction {
	// Dashboard
	DASHBOARD = 'DASHBOARD',
	PROFILE = 'PROFILE',
	CHAT = 'CHAT',
	CHAT_ARCHIVED = 'CHAT_ARCHIVED',
	GUIDE = 'GUIDE',

	// Clients
	CLIENT_LIST = 'CLIENT_LIST',
	CLIENT_MAP = 'CLIENT_MAP',
	LEAD_FINDER = 'LEAD_FINDER',
	CLIENT_SAVED_LISTS = 'CLIENT_SAVED_LISTS',
	CLIENT_SAVED_LIST_DETAIL = 'CLIENT_SAVED_LIST_DETAIL',
	CLIENT_DETAIL = 'CLIENT_DETAIL',
	CLIENT_INSIGHTS = 'CLIENT_INSIGHTS',
	CLIENT_ACTIVITIES = 'CLIENT_ACTIVITIES',
	CLIENT_COMMUNICATIONS = 'CLIENT_COMMUNICATIONS',
	CLIENT_ITEM_HISTORY = 'CLIENT_ITEM_HISTORY',
	CLIENT_CATEGORY_INSIGHTS = 'CLIENT_CATEGORY_INSIGHTS',
	CLIENT_VENDOR_SALES = 'CLIENT_VENDOR_SALES',
	CLIENT_PRICE_CHANGES = 'CLIENT_PRICE_CHANGES',
	CLIENT_SHOP = 'CLIENT_SHOP',
	CLIENT_CART = 'CLIENT_CART',
	CLIENT_CONTACTS = 'CLIENT_CONTACTS',
	CLIENT_NOTES = 'CLIENT_NOTES',
	CLIENT_EMAILS = 'CLIENT_EMAILS',
	CLIENT_TASKS = 'CLIENT_TASKS',
	CLIENT_VISITS = 'CLIENT_VISITS',
	CLIENT_QUOTES = 'CLIENT_QUOTES',
	CLIENT_OPPORTUNITIES = 'CLIENT_OPPORTUNITIES',
	CLIENT_JOBS = 'CLIENT_JOBS',
	CLIENT_ORDERS = 'CLIENT_ORDERS',
	CLIENT_INVOICES = 'CLIENT_INVOICES',
	CLIENT_STATEMENTS = 'CLIENT_STATEMENTS',
	CLIENT_FILES = 'CLIENT_FILES',
	CLIENT_FORMS = 'CLIENT_FORMS',
	CLIENT_LICENSES = 'CLIENT_LICENSES',
	CLIENT_LOCATIONS = 'CLIENT_LOCATIONS',
	CLIENT_CHILD_ACCOUNTS = 'CLIENT_CHILD_ACCOUNTS',
	CLIENT_OWNERSHIP = 'CLIENT_OWNERSHIP',
	CLIENT_DISTRIBUTION = 'CLIENT_DISTRIBUTION',
	CLIENT_SERVICE = 'CLIENT_SERVICE',
	CLIENT_PAYMENTS = 'CLIENT_PAYMENTS',
	CLIENT_ISSUES = 'CLIENT_ISSUES',
	CLIENT_SOURCING_REQUESTS = 'CLIENT_SOURCING_REQUESTS',
	CLIENT_COMPETITORS = 'CLIENT_COMPETITORS',
	CLIENT_COMPETITIVE_PRICES = 'CLIENT_COMPETITIVE_PRICES',
	CLIENT_PRICE_CONTRACTS = 'CLIENT_PRICE_CONTRACTS',
	CLIENT_ITEM_NUMBERS = 'CLIENT_ITEM_NUMBERS',
	CLIENT_STOREFRONT_USERS = 'CLIENT_STOREFRONT_USERS',
	OWNERSHIP_LIST = 'OWNERSHIP_LIST',
	OWNERSHIP_DETAIL = 'OWNERSHIP_DETAIL',
	OWNERSHIP_CLIENTS = 'OWNERSHIP_CLIENTS',
	VISIT_LIST = 'VISIT_LIST',
	VISIT_DETAIL = 'VISIT_DETAIL',

	// Contacts
	CONTACT_LIST = 'CONTACT_LIST',
	CONTACT_DETAIL = 'CONTACT_DETAIL',
	CONTACT_SCAN = 'CONTACT_SCAN',

	// Orders
	ORDER_LIST = 'ORDER_LIST',
	ORDER_FULFILLMENT = 'ORDER_FULFILLMENT',
	ORDER_NEW = 'ORDER_NEW',
	ORDER_DETAIL = 'ORDER_DETAIL',
	ORDER_EDIT = 'ORDER_EDIT',
	ORDER_NOTES = 'ORDER_NOTES',
	ORDER_PAYMENTS = 'ORDER_PAYMENTS',
	ORDER_INVOICES = 'ORDER_INVOICES',
	ORDER_LICENSES = 'ORDER_LICENSES',

	// Quotes
	QUOTE_LIST = 'QUOTE_LIST',
	QUOTE_DETAIL = 'QUOTE_DETAIL',
	QUOTE_EDIT = 'QUOTE_EDIT',
	QUOTE_MATCHMAKER = 'QUOTE_MATCHMAKER',
	QUOTE_REQUEST_LIST = 'QUOTE_REQUEST_LIST',
	QUOTE_REQUEST_DETAIL = 'QUOTE_REQUEST_DETAIL',

	// Invoices
	INVOICE_LIST = 'INVOICE_LIST',
	INVOICE_DETAIL = 'INVOICE_DETAIL',
	INVOICE_LINES = 'INVOICE_LINES',
	INVOICE_SHOP = 'INVOICE_SHOP',
	INVOICE_EDIT = 'INVOICE_EDIT',
	INVOICE_PAYMENTS = 'INVOICE_PAYMENTS',
	INVOICE_ORDERS = 'INVOICE_ORDERS',
	INVOICE_MAKE_PAYMENT = 'INVOICE_MAKE_PAYMENT',
	STATEMENT_LIST = 'STATEMENT_LIST',
	STATEMENT_DETAIL = 'STATEMENT_DETAIL',

	// Payments
	TRANSACTION_LIST = 'TRANSACTION_LIST',
	TRANSACTION_DETAIL = 'TRANSACTION_DETAIL',
	TRANSACTION_RELATED = 'TRANSACTION_RELATED',
	TRANSACTION_HISTORY = 'TRANSACTION_HISTORY',
	MAKE_PAYMENT = 'MAKE_PAYMENT',
	CYCLE_CHARGES = 'CYCLE_CHARGES',

	// Items
	ITEM_LIST = 'ITEM_LIST',
	ITEM_CATEGORIES = 'ITEM_CATEGORIES',
	ITEM_CATALOG_LINKS = 'ITEM_CATALOG_LINKS',
	ITEM_CROSS_REFERENCE_LIST = 'ITEM_CROSS_REFERENCE_LIST',
	ITEM_SAVED_LISTS = 'ITEM_SAVED_LISTS',
	ITEM_SAVED_LIST_DETAIL = 'ITEM_SAVED_LIST_DETAIL',
	ITEM_DETAIL = 'ITEM_DETAIL',
	ITEM_INSIGHTS = 'ITEM_INSIGHTS',
	ITEM_CLIENT_HISTORY = 'ITEM_CLIENT_HISTORY',
	ITEM_FREQUENTLY_PURCHASED = 'ITEM_FREQUENTLY_PURCHASED',
	ITEM_IMAGES = 'ITEM_IMAGES',
	ITEM_LOTS = 'ITEM_LOTS',
	ITEM_WAREHOUSES = 'ITEM_WAREHOUSES',
	ITEM_PRICE_CONTRACTS = 'ITEM_PRICE_CONTRACTS',
	ITEM_PRICE_HISTORY = 'ITEM_PRICE_HISTORY',
	ITEM_COMPETITIVE_PRICES = 'ITEM_COMPETITIVE_PRICES',
	ITEM_CROSS_REFERENCES = 'ITEM_CROSS_REFERENCES',
	ITEM_INVENTORY_HISTORY = 'ITEM_INVENTORY_HISTORY',
	ITEM_FILES = 'ITEM_FILES',
	ITEM_SOURCING_REQUESTS = 'ITEM_SOURCING_REQUESTS',
	ITEM_PURCHASE_ORDERS = 'ITEM_PURCHASE_ORDERS',
	STOREFRONT_ITEMS = 'STOREFRONT_ITEMS',
	STOREFRONT_ITEM_DETAIL = 'STOREFRONT_ITEM_DETAIL',

	// Vendors
	VENDOR_LIST = 'VENDOR_LIST',
	VENDOR_DETAIL = 'VENDOR_DETAIL',
	VENDOR_ITEMS = 'VENDOR_ITEMS',
	VENDOR_PURCHASE_ORDERS = 'VENDOR_PURCHASE_ORDERS',
	VENDOR_RECEIPTS = 'VENDOR_RECEIPTS',
	VENDOR_ISSUES = 'VENDOR_ISSUES',
	VENDOR_ACTIVITIES = 'VENDOR_ACTIVITIES',
	VENDOR_SOURCING_REQUESTS = 'VENDOR_SOURCING_REQUESTS',
	VENDOR_COMMUNICATIONS = 'VENDOR_COMMUNICATIONS',
	VENDOR_CALL_HISTORY = 'VENDOR_CALL_HISTORY',
	VENDOR_EMAILS = 'VENDOR_EMAILS',

	// Competitors
	COMPETITOR_LIST = 'COMPETITOR_LIST',
	COMPETITOR_DETAIL = 'COMPETITOR_DETAIL',
	COMPETITOR_CUSTOMERS = 'COMPETITOR_CUSTOMERS',
	COMPETITOR_PRICES = 'COMPETITOR_PRICES',
	COMPETITOR_NOTES = 'COMPETITOR_NOTES',

	// Tasks
	TASK_LIST = 'TASK_LIST',
	TASK_DETAIL = 'TASK_DETAIL',

	// Issues
	ISSUE_LIST = 'ISSUE_LIST',
	ISSUE_DETAIL = 'ISSUE_DETAIL',
	ISSUE_ACTIVITIES = 'ISSUE_ACTIVITIES',
	ISSUE_NOTES = 'ISSUE_NOTES',
	ISSUE_CONVERSATION = 'ISSUE_CONVERSATION',
	ISSUE_TASKS = 'ISSUE_TASKS',
	ISSUE_LINKED = 'ISSUE_LINKED',
	ISSUE_FILES = 'ISSUE_FILES',

	// Purchase Orders
	PURCHASE_ORDER_LIST = 'PURCHASE_ORDER_LIST',
	PURCHASE_ORDER_DETAIL = 'PURCHASE_ORDER_DETAIL',
	PURCHASE_ORDER_RECEIPTS = 'PURCHASE_ORDER_RECEIPTS',
	PURCHASE_ORDER_ISSUES = 'PURCHASE_ORDER_ISSUES',
	RECEIPT_LIST = 'RECEIPT_LIST',
	RECEIPT_DETAIL = 'RECEIPT_DETAIL',
	RECEIPT_LINES = 'RECEIPT_LINES',
	RECEIPT_ISSUES = 'RECEIPT_ISSUES',
	RECEIPT_DOCUMENTS = 'RECEIPT_DOCUMENTS',
	RECEIPT_VENDOR_EMAILS = 'RECEIPT_VENDOR_EMAILS',
	RECEIPT_ACTIVITY = 'RECEIPT_ACTIVITY',

	// Sourcing
	SOURCING_REQUEST_LIST = 'SOURCING_REQUEST_LIST',
	SOURCING_REQUEST_DETAIL = 'SOURCING_REQUEST_DETAIL',
	SOURCING_REQUEST_LINES = 'SOURCING_REQUEST_LINES',
	SOURCING_REQUEST_RFQS = 'SOURCING_REQUEST_RFQS',
	SOURCING_REQUEST_NOTES = 'SOURCING_REQUEST_NOTES',
	SOURCING_REQUEST_TASKS = 'SOURCING_REQUEST_TASKS',
	SOURCING_REQUEST_ISSUES = 'SOURCING_REQUEST_ISSUES',
	SOURCING_REQUEST_FILES = 'SOURCING_REQUEST_FILES',

	// Pricing
	PRICE_CONTRACT_LIST = 'PRICE_CONTRACT_LIST',
	PRICE_CONTRACT_DETAIL = 'PRICE_CONTRACT_DETAIL',
	PRICE_CONTRACT_CLIENTS = 'PRICE_CONTRACT_CLIENTS',
	PRICE_CONTRACT_ITEMS = 'PRICE_CONTRACT_ITEMS',
	PROMO_DETAIL = 'PROMO_DETAIL',

	// Opportunities
	OPPORTUNITY_LIST = 'OPPORTUNITY_LIST',
	OPPORTUNITY_DETAIL = 'OPPORTUNITY_DETAIL',
	OPPORTUNITY_JOB = 'OPPORTUNITY_JOB',
	OPPORTUNITY_QUOTES = 'OPPORTUNITY_QUOTES',
	OPPORTUNITY_INVOICES = 'OPPORTUNITY_INVOICES',
	OPPORTUNITY_BIDS = 'OPPORTUNITY_BIDS',
	OPPORTUNITY_NOTES = 'OPPORTUNITY_NOTES',
	OPPORTUNITY_TASKS = 'OPPORTUNITY_TASKS',
	OPPORTUNITY_FILES = 'OPPORTUNITY_FILES',
	OPPORTUNITY_ACTIVITY = 'OPPORTUNITY_ACTIVITY',

	// Trade shows
	TRADE_SHOW_LIST = 'TRADE_SHOW_LIST',
	TRADE_SHOW_DETAIL = 'TRADE_SHOW_DETAIL',
	ASSOCIATION_LIST = 'ASSOCIATION_LIST',
	ASSOCIATION_DETAIL = 'ASSOCIATION_DETAIL',

	// Jobs
	JOB_LIST = 'JOB_LIST',
	JOB_POSSIBLE = 'JOB_POSSIBLE',
	JOB_DETAIL = 'JOB_DETAIL',
	JOB_SUB_JOBS = 'JOB_SUB_JOBS',
	JOB_QUOTES = 'JOB_QUOTES',
	JOB_ACTIVITY = 'JOB_ACTIVITY',
	JOB_ORDERS = 'JOB_ORDERS',
	JOB_INVOICES = 'JOB_INVOICES',
	JOB_NOTES = 'JOB_NOTES',

	// Campaigns
	CAMPAIGN_LIST = 'CAMPAIGN_LIST',
	CAMPAIGN_INBOX = 'CAMPAIGN_INBOX',
	CAMPAIGN_DETAIL = 'CAMPAIGN_DETAIL',
	CAMPAIGN_BUILD = 'CAMPAIGN_BUILD',
	MARKETING_CAMPAIGN_LIST = 'MARKETING_CAMPAIGN_LIST',
	MARKETING_CAMPAIGN_DETAIL = 'MARKETING_CAMPAIGN_DETAIL',
	MARKETING_MEDIA = 'MARKETING_MEDIA',
	FORM_LIST = 'FORM_LIST',
	FORM_DETAIL = 'FORM_DETAIL',
	FORMS_SENT = 'FORMS_SENT',
	FORM_SUBMISSIONS = 'FORM_SUBMISSIONS',
	FORM_SUBMISSION_DETAIL = 'FORM_SUBMISSION_DETAIL',

	// Communications
	INBOX = 'INBOX',
	LIVE_CALLS = 'LIVE_CALLS',
	EMAIL_MAILBOX = 'EMAIL_MAILBOX',
	EMAIL_SENT = 'EMAIL_SENT',
	EMAIL_SENT_DETAIL = 'EMAIL_SENT_DETAIL',

	// Distribution
	DISTRIBUTION_ROUTES = 'DISTRIBUTION_ROUTES',
	DISTRIBUTION_MAP = 'DISTRIBUTION_MAP',
	DISTRIBUTION_LIVE_BOARD = 'DISTRIBUTION_LIVE_BOARD',
	DISTRIBUTION_MY_TRIPS = 'DISTRIBUTION_MY_TRIPS',
	DISTRIBUTION_DISPATCH = 'DISTRIBUTION_DISPATCH',
	DISTRIBUTION_SCHEDULE = 'DISTRIBUTION_SCHEDULE',
	DISTRIBUTION_REPLENISHMENT = 'DISTRIBUTION_REPLENISHMENT',
	DISTRIBUTION_REBALANCE = 'DISTRIBUTION_REBALANCE',
	DISTRIBUTION_ASSIGN_UNROUTED = 'DISTRIBUTION_ASSIGN_UNROUTED',
	ROUTE_DETAIL = 'ROUTE_DETAIL',
	ROUTE_STOPS = 'ROUTE_STOPS',
	TRIP_LIST = 'TRIP_LIST',
	TRIP_FULFILLMENT = 'TRIP_FULFILLMENT',
	TRIP_MAP = 'TRIP_MAP',
	TRIP_DETAIL = 'TRIP_DETAIL',
	TRIP_STOPS = 'TRIP_STOPS',
	TRIP_DRIVER = 'TRIP_DRIVER',
	TRIP_VEHICLE = 'TRIP_VEHICLE',
	TRIP_NOTES = 'TRIP_NOTES',
	TRIP_PHOTOS = 'TRIP_PHOTOS',
	TRIP_HISTORY = 'TRIP_HISTORY',
	TRIP_INVENTORY = 'TRIP_INVENTORY',
	DRIVER_LIST = 'DRIVER_LIST',
	DRIVER_DETAIL = 'DRIVER_DETAIL',
	DRIVER_ROUTES = 'DRIVER_ROUTES',
	DRIVER_TRIPS = 'DRIVER_TRIPS',
	VEHICLE_LIST = 'VEHICLE_LIST',
	VEHICLE_MAP = 'VEHICLE_MAP',
	VEHICLE_DETAIL = 'VEHICLE_DETAIL',
	VEHICLE_ROUTES = 'VEHICLE_ROUTES',
	VEHICLE_TRIPS = 'VEHICLE_TRIPS',
	VEHICLE_ITEMS = 'VEHICLE_ITEMS',
	VEHICLE_ISSUES = 'VEHICLE_ISSUES',
	MERCHANDISER_LIST = 'MERCHANDISER_LIST',
	MERCHANDISER_MAP = 'MERCHANDISER_MAP',
	MERCHANDISER_DETAIL = 'MERCHANDISER_DETAIL',
	MERCHANDISER_TEMPLATES = 'MERCHANDISER_TEMPLATES',
	SENSOR_DEVICE_LIST = 'SENSOR_DEVICE_LIST',
	SENSOR_DEVICE_DETAIL = 'SENSOR_DEVICE_DETAIL',
	SENSOR_MODULE_FAULTS = 'SENSOR_MODULE_FAULTS',

	// Warehouses
	WAREHOUSE_LIST = 'WAREHOUSE_LIST',
	WAREHOUSE_DETAIL = 'WAREHOUSE_DETAIL',
	WAREHOUSE_ITEMS = 'WAREHOUSE_ITEMS',
	WAREHOUSE_LOTS = 'WAREHOUSE_LOTS',
	WAREHOUSE_LOCATIONS = 'WAREHOUSE_LOCATIONS',
	WAREHOUSE_MOVEMENTS = 'WAREHOUSE_MOVEMENTS',
	LOCATION_LIST = 'LOCATION_LIST',
	LOCATION_MAP = 'LOCATION_MAP',
	LOCATION_DETAIL = 'LOCATION_DETAIL',
	LOCATION_ITEMS = 'LOCATION_ITEMS',
	LOCATION_LOTS = 'LOCATION_LOTS',
	LOCATION_SENSORS = 'LOCATION_SENSORS',
	LOCATION_SENSOR_DETAIL = 'LOCATION_SENSOR_DETAIL',
	LOCATION_SENSOR_HISTORY = 'LOCATION_SENSOR_HISTORY',

	// Sales Routes
	SALES_ROUTES = 'SALES_ROUTES',
	SALES_ROUTE_PLANNER = 'SALES_ROUTE_PLANNER',
	SALES_ROUTE_DETAIL = 'SALES_ROUTE_DETAIL',
	SALES_TRIP_DETAIL = 'SALES_TRIP_DETAIL',
	SALES_TRIP_RUN = 'SALES_TRIP_RUN',
	SALES_PLAN_DETAIL = 'SALES_PLAN_DETAIL',

	// Compliance
	COMPLIANCE_DASHBOARD = 'COMPLIANCE_DASHBOARD',
	COMPLIANCE_ATTENTION = 'COMPLIANCE_ATTENTION',
	COMPLIANCE_EXCLUSIONS = 'COMPLIANCE_EXCLUSIONS',
	COMPLIANCE_ERP_ACTIVITY = 'COMPLIANCE_ERP_ACTIVITY',
	COMPLIANCE_RECENT_CHANGES = 'COMPLIANCE_RECENT_CHANGES',
	LICENSE_REGISTRY = 'LICENSE_REGISTRY',
	LICENSE_LIST = 'LICENSE_LIST',
	LICENSE_MATCHING = 'LICENSE_MATCHING',
	LICENSE_MATCHES = 'LICENSE_MATCHES',
	LICENSE_DETAIL = 'LICENSE_DETAIL',
	LICENSE_LINKS = 'LICENSE_LINKS',
	LICENSE_NOTES = 'LICENSE_NOTES',
	LICENSE_TASKS = 'LICENSE_TASKS',
	LICENSE_DISCREPANCIES = 'LICENSE_DISCREPANCIES',
	LICENSE_DISCIPLINARY = 'LICENSE_DISCIPLINARY',
	LICENSE_AUDIT = 'LICENSE_AUDIT',
	LICENSE_FILES = 'LICENSE_FILES',

	// Automation
	AUTOMATION_HOME = 'AUTOMATION_HOME',
	AUTOMATION_FLOWS = 'AUTOMATION_FLOWS',
	AUTOMATION_FLOW_DETAIL = 'AUTOMATION_FLOW_DETAIL',
	AUTOMATION_TRIGGERS = 'AUTOMATION_TRIGGERS',
	AUTOMATION_TRIGGER_DETAIL = 'AUTOMATION_TRIGGER_DETAIL',
	AUTOMATION_CAMPAIGNS = 'AUTOMATION_CAMPAIGNS',
	AUTOMATION_CAMPAIGN_DETAIL = 'AUTOMATION_CAMPAIGN_DETAIL',
	AUTOMATION_TARGET_LISTS = 'AUTOMATION_TARGET_LISTS',
	AUTOMATION_TARGET_LIST_DETAIL = 'AUTOMATION_TARGET_LIST_DETAIL',
	AUTOMATION_EXECUTIONS = 'AUTOMATION_EXECUTIONS',

	// Ai Agents
	AI_AGENTS_HOME = 'AI_AGENTS_HOME',
	AI_AGENT_LIST = 'AI_AGENT_LIST',
	AI_AGENT_DETAIL = 'AI_AGENT_DETAIL',
	AI_CONVERSATION_LIST = 'AI_CONVERSATION_LIST',
	AI_CONVERSATION_DETAIL = 'AI_CONVERSATION_DETAIL',
	AI_ANALYTICS = 'AI_ANALYTICS',

	// Reports
	REPORTS_DASHBOARD = 'REPORTS_DASHBOARD',
	REPORT_LIST = 'REPORT_LIST',
	REPORT_CATEGORY = 'REPORT_CATEGORY',
	REPORT_PAGE = 'REPORT_PAGE',

	// Settings
	SETTINGS = 'SETTINGS',
	SETTINGS_PAGE = 'SETTINGS_PAGE',
	GROUP_DETAIL = 'GROUP_DETAIL',
	TEAM_DETAIL = 'TEAM_DETAIL',
	BRAND_DETAIL = 'BRAND_DETAIL',
	STOREFRONT_USER_DETAIL = 'STOREFRONT_USER_DETAIL',
}

/**
 * Parameter definition for navigation routes
 */
export interface NavigationParamDefinition {
	name: string;
	type: 'number' | 'string';
	description: string;
}

/**
 * What a screen needs. Each list is one guard on the way to the screen (a route, the
 * route around it, the setting that owns it): one entry from every list has to hold,
 * which is how the app's route guards read. The agent is offered a screen only when
 * these hold, and the navigate tool refuses it otherwise, so it never opens a page the
 * app would turn away.
 */
export interface NavigationGuardsModel {
	requiredPermissions?: ReadonlyArray<ReadonlyArray<string>>;
	requiredCapabilities?: ReadonlyArray<ReadonlyArray<CapabilityAlias>>;
}

/** One screen of a route that stands for many (a settings page, a report). */
export interface NavigationPageModel extends NavigationGuardsModel {
	value: string;
}

/**
 * Navigation route definition
 */
export interface NavigationRouteDefinition extends NavigationGuardsModel {
	action: NavigationAction;
	category: NavigationCategory;
	routeTemplate: string;
	requiredParams: Array<NavigationParamDefinition>;
	/** Said beside the action, only where its name doesn't say what the screen is. */
	hint?: string;
	/** The values of the route's one param, each its own screen with its own guards. */
	pages?: ReadonlyArray<NavigationPageModel>;
}

/**
 * Navigation execute result
 */
export interface NavigationExecuteResult {
	success: boolean;
	navigated?: boolean;
	url?: string;
	action?: NavigationAction;
	external?: boolean;
	error?: string;
	missingParams?: Array<NavigationParamDefinition>;
}

/**
 * Navigation event emitted to the frontend
 */
export interface NavigationEvent {
	url: string;
	action: NavigationAction;
	external: boolean;
}

/** Permission groups several screens share. */
const ACCESS_LOGS_READ: ReadonlyArray<string> = [RbacActions.ACCESS_LOGS.LOGIN_HISTORY.ECOMMERCE_READ.action, RbacActions.ACCESS_LOGS.LOGIN_HISTORY.READ.action, RbacActions.ACCESS_LOGS.READ.action];
const INVOICES_VIEW: ReadonlyArray<string> = [RbacActions.INVOICES.VIEW_ANY.action, RbacActions.INVOICES.VIEW_OWN.action];
const JOBS_READ: ReadonlyArray<string> = [RbacActions.JOBS.READ.action];
const LICENSES_VIEW_ANY: ReadonlyArray<string> = [RbacActions.LICENSES.VIEW_ANY.action];
const MARKETING_CAMPAIGNS_READ: ReadonlyArray<string> = [RbacActions.MARKETING.CAMPAIGNS.READ.action];
const ORDERS_VIEW: ReadonlyArray<string> = [RbacActions.ORDERS.VIEW_ANY.action, RbacActions.ORDERS.VIEW_OWN.action];
const PHONE_SYSTEM_CONFIGURE: ReadonlyArray<string> = [RbacActions.PHONE.SYSTEM_CONFIGURE.action];
const QUOTES_VIEW: ReadonlyArray<string> = [RbacActions.QUOTES.VIEW_ANY.action, RbacActions.QUOTES.VIEW_OWN.action];
const SALES_TRIPS_VIEW: ReadonlyArray<string> = [RbacActions.SALES_TRIPS.VIEW_ANY.action, RbacActions.SALES_TRIPS.VIEW_SELF.action];
const SETTINGS_INTEGRATIONS_MANAGE: ReadonlyArray<string> = [RbacActions.SETTINGS.INTEGRATIONS.MANAGE.action];
const TEAMS_READ: ReadonlyArray<string> = [RbacActions.TEAMS.READ.action];
const TRANSACTIONS_VIEW: ReadonlyArray<string> = [RbacActions.TRANSACTIONS.VIEW_ANY.action, RbacActions.TRANSACTIONS.VIEW_OWN.action];

/** The report categories, /reporting/categories/<value>. */
export const NAVIGATION_REPORT_CATEGORY_PAGES: ReadonlyArray<NavigationPageModel> = [
	{ value: 'accounting' },
	{ value: 'analytics' },
	{ value: 'communications' },
	{ value: 'custom' },
	{ value: 'customers' },
	{ value: 'geography' },
	{ value: 'inventory' },
	{ value: 'operations' },
	{ value: 'purchasing' },
	{ value: 'receiving' },
	{ value: 'sales' },
	{ value: 'scorecards' },
];

/**
 * Report pages, /reporting/reports/<value>. Most have no guard on the route: the
 * report's own permission (the report registry in crm-api) decides, and the Navigate
 * tool applies it.
 */
export const NAVIGATION_REPORT_PAGES: ReadonlyArray<NavigationPageModel> = [
	{ value: 'abandoned-carts' },
	{ value: 'accounts-receivable-aging' },
	{ value: 'aging-open-issues' },
	{ value: 'at-risk-insights' },
	{ value: 'average-time-to-acknowledge' },
	{ value: 'awaiting-my-approval' },
	{ value: 'call-history', requiredPermissions: [[RbacActions.CALL_HISTORY.READ.action]] },
	{ value: 'cash-receipts' },
	{ value: 'client-payment-status' },
	{ value: 'communications-activity-summary' },
	{ value: 'communications-calls-by-number' },
	{ value: 'company-at-risk-insights' },
	{ value: 'company-breakdown-sales' },
	{ value: 'company-reorder-insights' },
	{ value: 'competitive-pricing' },
	{ value: 'credit-management' },
	{ value: 'credit-pending-at-risk' },
	{ value: 'cumulative-sales-vs-goal' },
	{ value: 'customer-analytics' },
	{ value: 'customer-funds-outstanding', requiredPermissions: [[RbacActions.SETTINGS.PAYMENT_CONFIGURATION.READ.action]] },
	{ value: 'customer-lifetime-value' },
	{ value: 'customer-reorder' },
	{ value: 'customer-retention' },
	{ value: 'customer-saved-prices' },
	{ value: 'customer-scorecard' },
	{ value: 'exceptions-by-type-30d' },
	{ value: 'goals-table' },
	{ value: 'gross-profit-detail' },
	{ value: 'inventory-valuation' },
	{ value: 'item-performance-analysis' },
	{ value: 'item-scorecard' },
	{ value: 'item-search-activity' },
	{ value: 'kpi-overview' },
	{ value: 'market-penetration' },
	{ value: 'new-clients' },
	{ value: 'open-orders-by-job' },
	{ value: 'open-receipt-issues' },
	{ value: 'open-srs-by-status' },
	{ value: 'order-reconciliation' },
	{ value: 'orders' },
	{ value: 'outstanding-vendor-issues-by-status' },
	{ value: 'outstanding-vendor-quotes' },
	{ value: 'pending-invoices' },
	{ value: 'pos-awaiting-first-receipt' },
	{ value: 'product-category-distribution' },
	{ value: 'product-movement' },
	{ value: 'promo-redemptions' },
	{ value: 'quote-demand-by-customer' },
	{ value: 'quote-item-demand-summary' },
	{ value: 'quote-order-activity' },
	{ value: 'quotes' },
	{ value: 'quotes-follow-up' },
	{ value: 'receipt-volume-this-week' },
	{ value: 'regional-sales' },
	{ value: 'reorder-alerts-insights' },
	{ value: 'sales-analysis' },
	{ value: 'sales-by-company' },
	{ value: 'sales-by-customer-product-group' },
	{ value: 'sales-by-job' },
	{ value: 'sales-by-origin' },
	{ value: 'sales-by-product' },
	{ value: 'sales-by-product-company' },
	{ value: 'sales-comparison-by-customer' },
	{ value: 'sales-pipeline' },
	{ value: 'sales-rep-scorecard' },
	{ value: 'shipping-zone-analysis' },
	{ value: 'sr-aging' },
	{ value: 'sr-lines-by-pipeline-stage' },
	{ value: 'stock-levels' },
	{ value: 'storefront-engagement' },
	{ value: 'tax-summary' },
	{ value: 'team-communications-overview' },
	{ value: 'territory-analysis' },
	{ value: 'top-clients-revenue' },
	{ value: 'top-performers' },
	{ value: 'top-vendors-by-outstanding-po-value' },
	{ value: 'user-activity' },
	{ value: 'vendor-issue-rate' },
	{ value: 'vendor-scorecard' },
	{ value: 'visit-log' },
	{ value: 'win-loss' },
];

/** Settings pages, /settings/<value>, with the guard of the setting that owns each. */
export const NAVIGATION_SETTINGS_PAGES: ReadonlyArray<NavigationPageModel> = [
	{ value: 'access-logs', requiredPermissions: [ACCESS_LOGS_READ] },
	{ value: 'agreements', requiredPermissions: [[RbacActions.SETTINGS.AGREEMENTS.MANAGE.action]] },
	{ value: 'ai-usage', requiredPermissions: [[RbacActions.AI.USAGE.VIEW_ALL.action]] },
	{ value: 'announcements', requiredPermissions: [[RbacActions.ANNOUNCEMENTS.READ.action]] },
	{ value: 'audit', requiredPermissions: [ACCESS_LOGS_READ] },
	{ value: 'authentication', requiredPermissions: [[RbacActions.SETTINGS.AUTHENTICATION.MANAGE.action, RbacActions.SETTINGS.SIGN_IN.MANAGE.action]] },
	{ value: 'brands', requiredPermissions: [[RbacActions.SETTINGS.BRANDS_STOREFRONTS.READ.action]], requiredCapabilities: [[Capabilities.ECOMMERCE, Capabilities.WEB]] },
	{ value: 'business-days', requiredPermissions: [[RbacActions.SETTINGS.BUSINESS_DAYS.READ.action]] },
	{ value: 'catalog-values', requiredPermissions: [[RbacActions.SETTINGS.CATALOG_VALUES.MANAGE.action]] },
	{ value: 'claude-connector', requiredPermissions: [SETTINGS_INTEGRATIONS_MANAGE], requiredCapabilities: [[Capabilities.AI_MCP]] },
	{ value: 'client-defaults', requiredPermissions: [[RbacActions.SETTINGS.CLIENT_DEFAULTS.MANAGE.action]] },
	{ value: 'communications-phone-system', requiredPermissions: [PHONE_SYSTEM_CONFIGURE], requiredCapabilities: [[Capabilities.SOFTPHONE]] },
	{ value: 'communications-port-numbers', requiredPermissions: [PHONE_SYSTEM_CONFIGURE], requiredCapabilities: [[Capabilities.SOFTPHONE]] },
	{ value: 'communications-text-messages', requiredPermissions: [PHONE_SYSTEM_CONFIGURE], requiredCapabilities: [[Capabilities.SMS]] },
	{ value: 'company-profile', requiredPermissions: [[RbacActions.SETTINGS.COMPANY_PROFILE.MANAGE.action]] },
	{ value: 'custom-fields', requiredPermissions: [[RbacActions.CUSTOM_FIELDS.VIEW_EDIT.action]] },
	{ value: 'default-order-status', requiredPermissions: [[RbacActions.SETTINGS.ORDER_SUBMISSION_CHECKS.MANAGE.action]] },
	{ value: 'distribution', requiredPermissions: [[RbacActions.SETTINGS.DISTRIBUTION_CONFIG.MANAGE.action]], requiredCapabilities: [[Capabilities.DISTRIBUTION]] },
	{ value: 'email', requiredPermissions: [[RbacActions.EMAIL.MAILBOX.action]] },
	{ value: 'email-domains', requiredPermissions: [[RbacActions.EMAIL.DOMAINS.READ.action]] },
	{ value: 'email-templates', requiredPermissions: [[RbacActions.EMAIL.TEMPLATES.READ.action]] },
	{ value: 'forms-privacy', requiredPermissions: [[RbacActions.SETTINGS.WEB.READ.action]], requiredCapabilities: [[Capabilities.ECOMMERCE, Capabilities.MARKETING, Capabilities.WEB]] },
	{ value: 'groups', requiredPermissions: [[RbacActions.GROUPS.READ.action]] },
	{ value: 'integrations', requiredPermissions: [SETTINGS_INTEGRATIONS_MANAGE] },
	{ value: 'integrations/apollo', requiredPermissions: [SETTINGS_INTEGRATIONS_MANAGE] },
	{ value: 'integrations/instantly', requiredPermissions: [SETTINGS_INTEGRATIONS_MANAGE] },
	{ value: 'integrations/millionverifier', requiredPermissions: [SETTINGS_INTEGRATIONS_MANAGE] },
	{ value: 'integrations/quickbooks', requiredPermissions: [SETTINGS_INTEGRATIONS_MANAGE] },
	{ value: 'languages', requiredPermissions: [[RbacActions.SETTINGS.LANGUAGES.MANAGE.action]] },
	{ value: 'lead-eligibility', requiredPermissions: [[RbacActions.USER.LEAD_ELIGIBILITY_MANAGE.action]] },
	{ value: 'login-history', requiredPermissions: [ACCESS_LOGS_READ] },
	{ value: 'notifications/recipients', requiredPermissions: [[RbacActions.SETTINGS.NOTIFICATION_RECIPIENTS.MANAGE.action]] },
	{ value: 'notifications/sensor-alerts', requiredPermissions: [[RbacActions.MERCHANDISERS.UPDATE.action]], requiredCapabilities: [[Capabilities.EQUIPMENT]] },
	{ value: 'order-notes', requiredPermissions: [[RbacActions.SETTINGS.ORDER_NOTES.MANAGE.action]] },
	{ value: 'order-submission-checks', requiredPermissions: [[RbacActions.SETTINGS.ORDER_SUBMISSION_CHECKS.MANAGE.action]] },
	{ value: 'payment-method-types', requiredPermissions: [[RbacActions.SETTINGS.PAYMENT_CONFIGURATION.READ.action]] },
	{ value: 'payment-rules', requiredPermissions: [[RbacActions.SETTINGS.PAYMENT_RULES.MANAGE.action]] },
	{ value: 'payments', requiredPermissions: [[RbacActions.SETTINGS.PAYMENT_CONFIGURATION.READ.action]] },
	{ value: 'pdf-templates', requiredPermissions: [[RbacActions.SETTINGS.PDF_TEMPLATES.READ.action]] },
	{ value: 'phone-provisioning', requiredPermissions: [PHONE_SYSTEM_CONFIGURE], requiredCapabilities: [[Capabilities.PHONE]] },
	{ value: 'po-approval-checks', requiredPermissions: [[RbacActions.SETTINGS.PURCHASE_ORDER_APPROVAL_CHECKS.MANAGE.action]] },
	{ value: 'promos', requiredPermissions: [[RbacActions.PROMOS.READ.action]], requiredCapabilities: [[Capabilities.PROMOTIONS]] },
	{ value: 'rbac/roles', requiredPermissions: [[RbacActions.ROLES.READ.action]] },
	{ value: 'rfqs/inbox', requiredPermissions: [[RbacActions.QUOTE_REQUESTS.INBOX.READ.action]] },
	{ value: 'rfqs/inbox/new', requiredPermissions: [[RbacActions.QUOTE_REQUESTS.INBOX.READ.action]] },
	{ value: 'rfqs/product-specs', requiredPermissions: [[RbacActions.ITEM_MATCHING.READ.action]] },
	{ value: 'saved-price-cost-changes', requiredPermissions: [[RbacActions.SETTINGS.SAVED_PRICE_COST_CHANGES.MANAGE.action]], requiredCapabilities: [[Capabilities.SALES]] },
	{ value: 'sensor-firmware', requiredPermissions: [[RbacActions.SETTINGS.SENSOR_FIRMWARE.READ.action]], requiredCapabilities: [[Capabilities.EQUIPMENT]] },
	{ value: 'shipping-methods', requiredPermissions: [[RbacActions.SETTINGS.SHIPPING_METHODS.MANAGE.action]] },
	{ value: 'statements', requiredPermissions: [[RbacActions.STATEMENTS.CREATE.action]] },
	{ value: 'storefront-designs', requiredPermissions: [[RbacActions.SETTINGS.STOREFRONT_DESIGNS.MANAGE.action]], requiredCapabilities: [[Capabilities.ECOMMERCE]] },
	{ value: 'storefront-users', requiredPermissions: [[RbacActions.ECOMMERCE_USERS.READ.action]], requiredCapabilities: [[Capabilities.ECOMMERCE]] },
	{ value: 'support-requests', requiredPermissions: [[RbacActions.SETTINGS.SUPPORT_REQUESTS.MANAGE.action]] },
	{ value: 'target-customers', requiredPermissions: [[RbacActions.CLIENTS.LEADS.PROSPECT.action, RbacActions.SETTINGS.TARGET_PROFILE.MANAGE.action]] },
	{ value: 'team-settings', requiredPermissions: [TEAMS_READ] },
	{ value: 'teams', requiredPermissions: [TEAMS_READ] },
	{ value: 'teams/coverage', requiredPermissions: [TEAMS_READ] },
	{ value: 'teams/roles', requiredPermissions: [TEAMS_READ] },
	{ value: 'teams/types', requiredPermissions: [TEAMS_READ] },
	{ value: 'tenant-api-keys', requiredPermissions: [[RbacActions.SETTINGS.API_KEYS.READ.action]] },
	{ value: 'trade-shows', requiredPermissions: [[RbacActions.TRADE_SHOWS.SETTINGS.action]], requiredCapabilities: [[Capabilities.TRADE_SHOWS]] },
	{ value: 'terms-codes', requiredPermissions: [[RbacActions.SETTINGS.TERMS_CODES.MANAGE.action]] },
	{ value: 'users', requiredPermissions: [[RbacActions.USER.READ.action]] },
	{ value: 'view-layouts', requiredPermissions: [[RbacActions.SETTINGS.VIEW_LAYOUTS.MANAGE.action]] },
	{ value: 'visible-kpis', requiredPermissions: [[RbacActions.SETTINGS.KPI_VISIBILITY.READ.action]] },
];

/** A route entry; the params come from the template. */
function screen(
	action: NavigationAction,
	category: NavigationCategory,
	routeTemplate: string,
	options: Pick<NavigationRouteDefinition, 'requiredPermissions' | 'requiredCapabilities' | 'pages' | 'hint'> = {},
): NavigationRouteDefinition {
	return { action, category, routeTemplate, requiredParams: templateParams(routeTemplate), ...options };
}

/**
 * Central registry of all navigation routes. Every screen in the route configs is here
 * or in navigation-not-navigable.ts, with the guards its route has
 * (navigation-coverage.test.ts holds both to that).
 */
export const NavigationRoutes: Array<NavigationRouteDefinition> = [
	// Dashboard
	screen(NavigationAction.DASHBOARD, NavigationCategory.DASHBOARD, '/dashboard'),
	screen(NavigationAction.PROFILE, NavigationCategory.DASHBOARD, '/profile'),
	screen(NavigationAction.CHAT, NavigationCategory.DASHBOARD, '/chat'),
	screen(NavigationAction.CHAT_ARCHIVED, NavigationCategory.DASHBOARD, '/chat/archived'),
	screen(NavigationAction.GUIDE, NavigationCategory.DASHBOARD, '/readme'),
	// Clients
	screen(NavigationAction.CLIENT_LIST, NavigationCategory.CLIENTS, '/clients'),
	screen(NavigationAction.CLIENT_MAP, NavigationCategory.CLIENTS, '/clients/map'),
	screen(NavigationAction.LEAD_FINDER, NavigationCategory.CLIENTS, '/lead-finder', { requiredPermissions: [[RbacActions.LEAD_FINDER.SEARCH.action]], requiredCapabilities: [[Capabilities.COMPLIANCE]], hint: 'find new leads' }),
	screen(NavigationAction.CLIENT_SAVED_LISTS, NavigationCategory.CLIENTS, '/clients/lists'),
	screen(NavigationAction.CLIENT_SAVED_LIST_DETAIL, NavigationCategory.CLIENTS, '/clients/lists/{clientListId}'),
	screen(NavigationAction.CLIENT_DETAIL, NavigationCategory.CLIENTS, '/clients/{clientId}'),
	screen(NavigationAction.CLIENT_INSIGHTS, NavigationCategory.CLIENTS, '/clients/{clientId}/insights'),
	screen(NavigationAction.CLIENT_ACTIVITIES, NavigationCategory.CLIENTS, '/clients/{clientId}/activities'),
	screen(NavigationAction.CLIENT_COMMUNICATIONS, NavigationCategory.CLIENTS, '/clients/{clientId}/communications'),
	screen(NavigationAction.CLIENT_ITEM_HISTORY, NavigationCategory.CLIENTS, '/clients/{clientId}/item-history'),
	screen(NavigationAction.CLIENT_CATEGORY_INSIGHTS, NavigationCategory.CLIENTS, '/clients/{clientId}/category-insights'),
	screen(NavigationAction.CLIENT_VENDOR_SALES, NavigationCategory.CLIENTS, '/clients/{clientId}/vendor-sales'),
	screen(NavigationAction.CLIENT_PRICE_CHANGES, NavigationCategory.CLIENTS, '/clients/{clientId}/price-changes'),
	screen(NavigationAction.CLIENT_SHOP, NavigationCategory.CLIENTS, '/clients/{clientId}/shop', { hint: 'shop for the client' }),
	screen(NavigationAction.CLIENT_CART, NavigationCategory.CLIENTS, '/clients/{clientId}/cart'),
	screen(NavigationAction.CLIENT_CONTACTS, NavigationCategory.CLIENTS, '/clients/{clientId}/contacts'),
	screen(NavigationAction.CLIENT_NOTES, NavigationCategory.CLIENTS, '/clients/{clientId}/notes'),
	screen(NavigationAction.CLIENT_EMAILS, NavigationCategory.CLIENTS, '/clients/{clientId}/emails'),
	screen(NavigationAction.CLIENT_TASKS, NavigationCategory.CLIENTS, '/clients/{clientId}/tasks'),
	screen(NavigationAction.CLIENT_VISITS, NavigationCategory.CLIENTS, '/clients/{clientId}/visits'),
	screen(NavigationAction.CLIENT_QUOTES, NavigationCategory.CLIENTS, '/clients/{clientId}/quotes'),
	screen(NavigationAction.CLIENT_OPPORTUNITIES, NavigationCategory.CLIENTS, '/clients/{clientId}/opportunities'),
	screen(NavigationAction.CLIENT_JOBS, NavigationCategory.CLIENTS, '/clients/{clientId}/jobs', { requiredCapabilities: [[Capabilities.JOBS]] }),
	screen(NavigationAction.CLIENT_ORDERS, NavigationCategory.CLIENTS, '/clients/{clientId}/orders'),
	screen(NavigationAction.CLIENT_INVOICES, NavigationCategory.CLIENTS, '/clients/{clientId}/invoices'),
	screen(NavigationAction.CLIENT_STATEMENTS, NavigationCategory.CLIENTS, '/clients/{clientId}/statements'),
	screen(NavigationAction.CLIENT_FILES, NavigationCategory.CLIENTS, '/clients/{clientId}/files'),
	screen(NavigationAction.CLIENT_FORMS, NavigationCategory.CLIENTS, '/clients/{clientId}/forms'),
	screen(NavigationAction.CLIENT_LICENSES, NavigationCategory.CLIENTS, '/clients/{clientId}/licenses', { requiredCapabilities: [[Capabilities.COMPLIANCE, Capabilities.PHARMACY]] }),
	screen(NavigationAction.CLIENT_LOCATIONS, NavigationCategory.CLIENTS, '/clients/{clientId}/locations'),
	screen(NavigationAction.CLIENT_CHILD_ACCOUNTS, NavigationCategory.CLIENTS, '/clients/{clientId}/child-accounts'),
	screen(NavigationAction.CLIENT_OWNERSHIP, NavigationCategory.CLIENTS, '/clients/{clientId}/ownership'),
	screen(NavigationAction.CLIENT_DISTRIBUTION, NavigationCategory.CLIENTS, '/clients/{clientId}/distribution', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.CLIENT_SERVICE, NavigationCategory.CLIENTS, '/clients/{clientId}/service', { requiredCapabilities: [[Capabilities.SERVICE]] }),
	screen(NavigationAction.CLIENT_PAYMENTS, NavigationCategory.CLIENTS, '/clients/{clientId}/payments'),
	screen(NavigationAction.CLIENT_ISSUES, NavigationCategory.CLIENTS, '/clients/{clientId}/issues'),
	screen(NavigationAction.CLIENT_SOURCING_REQUESTS, NavigationCategory.CLIENTS, '/clients/{clientId}/sourcing-requests'),
	screen(NavigationAction.CLIENT_COMPETITORS, NavigationCategory.CLIENTS, '/clients/{clientId}/competitors'),
	screen(NavigationAction.CLIENT_COMPETITIVE_PRICES, NavigationCategory.CLIENTS, '/clients/{clientId}/competitive-prices'),
	screen(NavigationAction.CLIENT_PRICE_CONTRACTS, NavigationCategory.CLIENTS, '/clients/{clientId}/price-contracts'),
	screen(NavigationAction.CLIENT_ITEM_NUMBERS, NavigationCategory.CLIENTS, '/clients/{clientId}/item-numbers'),
	screen(NavigationAction.CLIENT_STOREFRONT_USERS, NavigationCategory.CLIENTS, '/clients/{clientId}/ecommerce-users', { requiredCapabilities: [[Capabilities.ECOMMERCE]] }),
	screen(NavigationAction.OWNERSHIP_LIST, NavigationCategory.CLIENTS, '/clients/ownerships'),
	screen(NavigationAction.OWNERSHIP_DETAIL, NavigationCategory.CLIENTS, '/clients/ownerships/{ownershipId}'),
	screen(NavigationAction.OWNERSHIP_CLIENTS, NavigationCategory.CLIENTS, '/clients/ownerships/{ownershipId}/clients'),
	screen(NavigationAction.VISIT_LIST, NavigationCategory.CLIENTS, '/clients/visits'),
	screen(NavigationAction.VISIT_DETAIL, NavigationCategory.CLIENTS, '/clients/visits/{visitId}'),
	// Contacts
	screen(NavigationAction.CONTACT_LIST, NavigationCategory.CONTACTS, '/clients/contacts'),
	screen(NavigationAction.CONTACT_DETAIL, NavigationCategory.CONTACTS, '/clients/contacts/{contactId}', { hint: 'a person\'s page' }),
	screen(NavigationAction.CONTACT_SCAN, NavigationCategory.CONTACTS, '/clients/contacts/scan', { hint: 'scan a business card or badge' }),
	// Orders
	screen(NavigationAction.ORDER_LIST, NavigationCategory.ORDERS, '/orders', { requiredPermissions: [ORDERS_VIEW] }),
	screen(NavigationAction.ORDER_FULFILLMENT, NavigationCategory.ORDERS, '/orders/fulfillment', { requiredPermissions: [[RbacActions.ORDERS.FULFILLMENT.VIEW.action], ORDERS_VIEW] }),
	screen(NavigationAction.ORDER_NEW, NavigationCategory.ORDERS, '/orders/new', { requiredPermissions: [ORDERS_VIEW] }),
	screen(NavigationAction.ORDER_DETAIL, NavigationCategory.ORDERS, '/orders/{orderId}', { requiredPermissions: [ORDERS_VIEW] }),
	screen(NavigationAction.ORDER_EDIT, NavigationCategory.ORDERS, '/orders/{orderId}/edit', { requiredPermissions: [ORDERS_VIEW] }),
	screen(NavigationAction.ORDER_NOTES, NavigationCategory.ORDERS, '/orders/{orderId}/notes', { requiredPermissions: [[RbacActions.ORDERS.NOTES.READ.action], ORDERS_VIEW] }),
	screen(NavigationAction.ORDER_PAYMENTS, NavigationCategory.ORDERS, '/orders/{orderId}/payments', { requiredPermissions: [ORDERS_VIEW, TRANSACTIONS_VIEW] }),
	screen(NavigationAction.ORDER_INVOICES, NavigationCategory.ORDERS, '/orders/{orderId}/order-invoices', { requiredPermissions: [ORDERS_VIEW] }),
	screen(NavigationAction.ORDER_LICENSES, NavigationCategory.ORDERS, '/orders/{orderId}/licenses', { requiredPermissions: [ORDERS_VIEW], requiredCapabilities: [[Capabilities.COMPLIANCE, Capabilities.PHARMACY]] }),
	// Quotes
	screen(NavigationAction.QUOTE_LIST, NavigationCategory.QUOTES, '/quotes', { requiredPermissions: [QUOTES_VIEW] }),
	screen(NavigationAction.QUOTE_DETAIL, NavigationCategory.QUOTES, '/quotes/{quoteId}', { requiredPermissions: [QUOTES_VIEW] }),
	screen(NavigationAction.QUOTE_EDIT, NavigationCategory.QUOTES, '/quotes/{quoteId}/edit', { requiredPermissions: [[RbacActions.QUOTES.UPDATE.action], QUOTES_VIEW] }),
	screen(NavigationAction.QUOTE_MATCHMAKER, NavigationCategory.QUOTES, '/quotes/{quoteId}/matchmaker', { requiredPermissions: [[RbacActions.QUOTES.ADD_CUSTOM_LINES.action], QUOTES_VIEW] }),
	screen(NavigationAction.QUOTE_REQUEST_LIST, NavigationCategory.QUOTES, '/quote-requests'),
	screen(NavigationAction.QUOTE_REQUEST_DETAIL, NavigationCategory.QUOTES, '/quote-requests/{quoteRequestId}'),
	// Invoices
	screen(NavigationAction.INVOICE_LIST, NavigationCategory.INVOICES, '/invoices', { requiredPermissions: [INVOICES_VIEW] }),
	screen(NavigationAction.INVOICE_DETAIL, NavigationCategory.INVOICES, '/invoices/{invoiceId}', { requiredPermissions: [INVOICES_VIEW] }),
	screen(NavigationAction.INVOICE_LINES, NavigationCategory.INVOICES, '/invoices/{invoiceId}/lines', { requiredPermissions: [INVOICES_VIEW] }),
	screen(NavigationAction.INVOICE_SHOP, NavigationCategory.INVOICES, '/invoices/{invoiceId}/shop', { requiredPermissions: [[RbacActions.INVOICES.UPDATE.action], INVOICES_VIEW] }),
	screen(NavigationAction.INVOICE_EDIT, NavigationCategory.INVOICES, '/invoices/{invoiceId}/edit', { requiredPermissions: [INVOICES_VIEW] }),
	screen(NavigationAction.INVOICE_PAYMENTS, NavigationCategory.INVOICES, '/invoices/{invoiceId}/payments', { requiredPermissions: [INVOICES_VIEW, TRANSACTIONS_VIEW] }),
	screen(NavigationAction.INVOICE_ORDERS, NavigationCategory.INVOICES, '/invoices/{invoiceId}/invoice-orders', { requiredPermissions: [INVOICES_VIEW] }),
	screen(NavigationAction.INVOICE_MAKE_PAYMENT, NavigationCategory.INVOICES, '/invoices/{invoiceId}/make-payment', { requiredPermissions: [[RbacActions.INVOICES.UPDATE.action], INVOICES_VIEW] }),
	screen(NavigationAction.STATEMENT_LIST, NavigationCategory.INVOICES, '/statements', { requiredPermissions: [[RbacActions.STATEMENTS.VIEW_ANY.action, RbacActions.STATEMENTS.VIEW_OWN.action]] }),
	screen(NavigationAction.STATEMENT_DETAIL, NavigationCategory.INVOICES, '/statements/{statementId}', { requiredPermissions: [[RbacActions.STATEMENTS.VIEW_ANY.action, RbacActions.STATEMENTS.VIEW_OWN.action]] }),
	// Payments
	screen(NavigationAction.TRANSACTION_LIST, NavigationCategory.PAYMENTS, '/transactions', { requiredPermissions: [TRANSACTIONS_VIEW] }),
	screen(NavigationAction.TRANSACTION_DETAIL, NavigationCategory.PAYMENTS, '/transactions/{transactionId}', { requiredPermissions: [TRANSACTIONS_VIEW] }),
	screen(NavigationAction.TRANSACTION_RELATED, NavigationCategory.PAYMENTS, '/transactions/{transactionId}/related', { requiredPermissions: [TRANSACTIONS_VIEW] }),
	screen(NavigationAction.TRANSACTION_HISTORY, NavigationCategory.PAYMENTS, '/transactions/{transactionId}/history', { requiredPermissions: [TRANSACTIONS_VIEW] }),
	screen(NavigationAction.MAKE_PAYMENT, NavigationCategory.PAYMENTS, '/payments/make-payment', { requiredPermissions: [[RbacActions.TRANSACTIONS.CREATE.action]] }),
	screen(NavigationAction.CYCLE_CHARGES, NavigationCategory.PAYMENTS, '/payments/cycle-charges', { requiredPermissions: [[RbacActions.TRANSACTIONS.PAYMENT_SCHEDULES.READ.action]] }),
	// Items
	screen(NavigationAction.ITEM_LIST, NavigationCategory.ITEMS, '/items'),
	screen(NavigationAction.ITEM_CATEGORIES, NavigationCategory.ITEMS, '/categories'),
	screen(NavigationAction.ITEM_CATALOG_LINKS, NavigationCategory.ITEMS, '/items/catalog-links', { requiredPermissions: [[RbacActions.ITEMS.CATALOG_MANAGE.action]] }),
	screen(NavigationAction.ITEM_CROSS_REFERENCE_LIST, NavigationCategory.ITEMS, '/items/cross-references', { requiredPermissions: [[RbacActions.ITEMS.READ.action]] }),
	screen(NavigationAction.ITEM_SAVED_LISTS, NavigationCategory.ITEMS, '/items/lists'),
	screen(NavigationAction.ITEM_SAVED_LIST_DETAIL, NavigationCategory.ITEMS, '/items/lists/{itemListId}'),
	screen(NavigationAction.ITEM_DETAIL, NavigationCategory.ITEMS, '/items/{itemId}'),
	screen(NavigationAction.ITEM_INSIGHTS, NavigationCategory.ITEMS, '/items/{itemId}/insights'),
	screen(NavigationAction.ITEM_CLIENT_HISTORY, NavigationCategory.ITEMS, '/items/{itemId}/client-history'),
	screen(NavigationAction.ITEM_FREQUENTLY_PURCHASED, NavigationCategory.ITEMS, '/items/{itemId}/frequently-purchased'),
	screen(NavigationAction.ITEM_IMAGES, NavigationCategory.ITEMS, '/items/{itemId}/images'),
	screen(NavigationAction.ITEM_LOTS, NavigationCategory.ITEMS, '/items/{itemId}/lots'),
	screen(NavigationAction.ITEM_WAREHOUSES, NavigationCategory.ITEMS, '/items/{itemId}/warehouses', { requiredPermissions: [[RbacActions.WAREHOUSES.READ.action]] }),
	screen(NavigationAction.ITEM_PRICE_CONTRACTS, NavigationCategory.ITEMS, '/items/{itemId}/price-contracts'),
	screen(NavigationAction.ITEM_PRICE_HISTORY, NavigationCategory.ITEMS, '/items/{itemId}/price-history'),
	screen(NavigationAction.ITEM_COMPETITIVE_PRICES, NavigationCategory.ITEMS, '/items/{itemId}/competitive-prices'),
	screen(NavigationAction.ITEM_CROSS_REFERENCES, NavigationCategory.ITEMS, '/items/{itemId}/cross-references', { requiredPermissions: [[RbacActions.ITEMS.READ.action]] }),
	screen(NavigationAction.ITEM_INVENTORY_HISTORY, NavigationCategory.ITEMS, '/items/{itemId}/inventory-history'),
	screen(NavigationAction.ITEM_FILES, NavigationCategory.ITEMS, '/items/{itemId}/files'),
	screen(NavigationAction.ITEM_SOURCING_REQUESTS, NavigationCategory.ITEMS, '/items/{itemId}/sourcing-requests'),
	screen(NavigationAction.ITEM_PURCHASE_ORDERS, NavigationCategory.ITEMS, '/items/{itemId}/purchase-orders', { requiredPermissions: [[RbacActions.PURCHASE_ORDERS.READ.action]] }),
	screen(NavigationAction.STOREFRONT_ITEMS, NavigationCategory.ITEMS, '/ecommerce', { hint: 'items on the storefront' }),
	screen(NavigationAction.STOREFRONT_ITEM_DETAIL, NavigationCategory.ITEMS, '/ecommerce/{itemId}'),
	// Vendors
	screen(NavigationAction.VENDOR_LIST, NavigationCategory.VENDORS, '/vendors'),
	screen(NavigationAction.VENDOR_DETAIL, NavigationCategory.VENDORS, '/vendors/{vendorId}'),
	screen(NavigationAction.VENDOR_ITEMS, NavigationCategory.VENDORS, '/vendors/{vendorId}/vendor-items'),
	screen(NavigationAction.VENDOR_PURCHASE_ORDERS, NavigationCategory.VENDORS, '/vendors/{vendorId}/vendor-purchase-orders'),
	screen(NavigationAction.VENDOR_RECEIPTS, NavigationCategory.VENDORS, '/vendors/{vendorId}/receipts'),
	screen(NavigationAction.VENDOR_ISSUES, NavigationCategory.VENDORS, '/vendors/{vendorId}/issues'),
	screen(NavigationAction.VENDOR_ACTIVITIES, NavigationCategory.VENDORS, '/vendors/{vendorId}/activities'),
	screen(NavigationAction.VENDOR_SOURCING_REQUESTS, NavigationCategory.VENDORS, '/vendors/{vendorId}/sourcing-requests'),
	screen(NavigationAction.VENDOR_COMMUNICATIONS, NavigationCategory.VENDORS, '/vendors/{vendorId}/communications'),
	screen(NavigationAction.VENDOR_CALL_HISTORY, NavigationCategory.VENDORS, '/vendors/{vendorId}/call-history'),
	screen(NavigationAction.VENDOR_EMAILS, NavigationCategory.VENDORS, '/vendors/{vendorId}/emails'),
	// Competitors
	screen(NavigationAction.COMPETITOR_LIST, NavigationCategory.COMPETITORS, '/competitors'),
	screen(NavigationAction.COMPETITOR_DETAIL, NavigationCategory.COMPETITORS, '/competitors/{competitorId}'),
	screen(NavigationAction.COMPETITOR_CUSTOMERS, NavigationCategory.COMPETITORS, '/competitors/{competitorId}/customers'),
	screen(NavigationAction.COMPETITOR_PRICES, NavigationCategory.COMPETITORS, '/competitors/{competitorId}/prices'),
	screen(NavigationAction.COMPETITOR_NOTES, NavigationCategory.COMPETITORS, '/competitors/{competitorId}/notes'),
	// Tasks
	screen(NavigationAction.TASK_LIST, NavigationCategory.TASKS, '/tasks'),
	screen(NavigationAction.TASK_DETAIL, NavigationCategory.TASKS, '/tasks?taskId={taskId}', { hint: 'the task open in the task list' }),
	// Issues
	screen(NavigationAction.ISSUE_LIST, NavigationCategory.ISSUES, '/issues'),
	screen(NavigationAction.ISSUE_DETAIL, NavigationCategory.ISSUES, '/issues/{issueId}'),
	screen(NavigationAction.ISSUE_ACTIVITIES, NavigationCategory.ISSUES, '/issues/{issueId}/activities'),
	screen(NavigationAction.ISSUE_NOTES, NavigationCategory.ISSUES, '/issues/{issueId}/notes'),
	screen(NavigationAction.ISSUE_CONVERSATION, NavigationCategory.ISSUES, '/issues/{issueId}/conversation'),
	screen(NavigationAction.ISSUE_TASKS, NavigationCategory.ISSUES, '/issues/{issueId}/tasks'),
	screen(NavigationAction.ISSUE_LINKED, NavigationCategory.ISSUES, '/issues/{issueId}/linked'),
	screen(NavigationAction.ISSUE_FILES, NavigationCategory.ISSUES, '/issues/{issueId}/files'),
	// Purchase Orders
	screen(NavigationAction.PURCHASE_ORDER_LIST, NavigationCategory.PURCHASE_ORDERS, '/purchase-orders'),
	screen(NavigationAction.PURCHASE_ORDER_DETAIL, NavigationCategory.PURCHASE_ORDERS, '/purchase-orders/{purchaseOrderId}'),
	screen(NavigationAction.PURCHASE_ORDER_RECEIPTS, NavigationCategory.PURCHASE_ORDERS, '/purchase-orders/{purchaseOrderId}/receipts'),
	screen(NavigationAction.PURCHASE_ORDER_ISSUES, NavigationCategory.PURCHASE_ORDERS, '/purchase-orders/{purchaseOrderId}/issues'),
	screen(NavigationAction.RECEIPT_LIST, NavigationCategory.PURCHASE_ORDERS, '/inventory/receipts'),
	screen(NavigationAction.RECEIPT_DETAIL, NavigationCategory.PURCHASE_ORDERS, '/inventory/receipts/{receiptId}'),
	screen(NavigationAction.RECEIPT_LINES, NavigationCategory.PURCHASE_ORDERS, '/inventory/receipts/{receiptId}/lines'),
	screen(NavigationAction.RECEIPT_ISSUES, NavigationCategory.PURCHASE_ORDERS, '/inventory/receipts/{receiptId}/issues'),
	screen(NavigationAction.RECEIPT_DOCUMENTS, NavigationCategory.PURCHASE_ORDERS, '/inventory/receipts/{receiptId}/documents'),
	screen(NavigationAction.RECEIPT_VENDOR_EMAILS, NavigationCategory.PURCHASE_ORDERS, '/inventory/receipts/{receiptId}/vendor-emails'),
	screen(NavigationAction.RECEIPT_ACTIVITY, NavigationCategory.PURCHASE_ORDERS, '/inventory/receipts/{receiptId}/activity'),
	// Sourcing
	screen(NavigationAction.SOURCING_REQUEST_LIST, NavigationCategory.SOURCING, '/sourcing-requests'),
	screen(NavigationAction.SOURCING_REQUEST_DETAIL, NavigationCategory.SOURCING, '/sourcing-requests/{sourcingRequestId}'),
	screen(NavigationAction.SOURCING_REQUEST_LINES, NavigationCategory.SOURCING, '/sourcing-requests/{sourcingRequestId}/lines'),
	screen(NavigationAction.SOURCING_REQUEST_RFQS, NavigationCategory.SOURCING, '/sourcing-requests/{sourcingRequestId}/rfqs'),
	screen(NavigationAction.SOURCING_REQUEST_NOTES, NavigationCategory.SOURCING, '/sourcing-requests/{sourcingRequestId}/notes'),
	screen(NavigationAction.SOURCING_REQUEST_TASKS, NavigationCategory.SOURCING, '/sourcing-requests/{sourcingRequestId}/tasks'),
	screen(NavigationAction.SOURCING_REQUEST_ISSUES, NavigationCategory.SOURCING, '/sourcing-requests/{sourcingRequestId}/issues'),
	screen(NavigationAction.SOURCING_REQUEST_FILES, NavigationCategory.SOURCING, '/sourcing-requests/{sourcingRequestId}/files'),
	// Pricing
	screen(NavigationAction.PRICE_CONTRACT_LIST, NavigationCategory.PRICING, '/price-contracts'),
	screen(NavigationAction.PRICE_CONTRACT_DETAIL, NavigationCategory.PRICING, '/price-contracts/{priceContractId}'),
	screen(NavigationAction.PRICE_CONTRACT_CLIENTS, NavigationCategory.PRICING, '/price-contracts/{priceContractId}/clients'),
	screen(NavigationAction.PRICE_CONTRACT_ITEMS, NavigationCategory.PRICING, '/price-contracts/{priceContractId}/items'),
	screen(NavigationAction.PROMO_DETAIL, NavigationCategory.PRICING, '/settings/promos/{promoId}', { requiredPermissions: [[RbacActions.PROMOS.READ.action]], requiredCapabilities: [[Capabilities.PROMOTIONS]] }),
	// Opportunities
	screen(NavigationAction.OPPORTUNITY_LIST, NavigationCategory.OPPORTUNITIES, '/opportunities'),
	screen(NavigationAction.OPPORTUNITY_DETAIL, NavigationCategory.OPPORTUNITIES, '/opportunities/{opportunityId}'),
	screen(NavigationAction.OPPORTUNITY_JOB, NavigationCategory.OPPORTUNITIES, '/opportunities/{opportunityId}/job'),
	screen(NavigationAction.OPPORTUNITY_QUOTES, NavigationCategory.OPPORTUNITIES, '/opportunities/{opportunityId}/quotes'),
	screen(NavigationAction.OPPORTUNITY_INVOICES, NavigationCategory.OPPORTUNITIES, '/opportunities/{opportunityId}/invoices'),
	screen(NavigationAction.OPPORTUNITY_BIDS, NavigationCategory.OPPORTUNITIES, '/opportunities/{opportunityId}/bids'),
	screen(NavigationAction.OPPORTUNITY_NOTES, NavigationCategory.OPPORTUNITIES, '/opportunities/{opportunityId}/notes'),
	screen(NavigationAction.OPPORTUNITY_TASKS, NavigationCategory.OPPORTUNITIES, '/opportunities/{opportunityId}/tasks'),
	screen(NavigationAction.OPPORTUNITY_FILES, NavigationCategory.OPPORTUNITIES, '/opportunities/{opportunityId}/files'),
	screen(NavigationAction.OPPORTUNITY_ACTIVITY, NavigationCategory.OPPORTUNITIES, '/opportunities/{opportunityId}/activity'),
	// Trade Shows
	screen(NavigationAction.TRADE_SHOW_LIST, NavigationCategory.TRADE_SHOWS, '/trade-shows', { requiredPermissions: [[RbacActions.TRADE_SHOWS.READ.action]], requiredCapabilities: [[Capabilities.TRADE_SHOWS]] }),
	screen(NavigationAction.TRADE_SHOW_DETAIL, NavigationCategory.TRADE_SHOWS, '/trade-shows/{tradeShowId}', { requiredPermissions: [[RbacActions.TRADE_SHOWS.READ.action]], requiredCapabilities: [[Capabilities.TRADE_SHOWS]] }),
	screen(NavigationAction.ASSOCIATION_LIST, NavigationCategory.TRADE_SHOWS, '/associations', { requiredPermissions: [[RbacActions.TRADE_SHOWS.READ.action]], requiredCapabilities: [[Capabilities.TRADE_SHOWS]] }),
	screen(NavigationAction.ASSOCIATION_DETAIL, NavigationCategory.TRADE_SHOWS, '/associations/{associationId}', { requiredPermissions: [[RbacActions.TRADE_SHOWS.READ.action]], requiredCapabilities: [[Capabilities.TRADE_SHOWS]] }),
	// Jobs
	screen(NavigationAction.JOB_LIST, NavigationCategory.JOBS, '/jobs', { requiredPermissions: [JOBS_READ], requiredCapabilities: [[Capabilities.JOBS]] }),
	screen(NavigationAction.JOB_POSSIBLE, NavigationCategory.JOBS, '/jobs/possible', { requiredPermissions: [JOBS_READ], requiredCapabilities: [[Capabilities.JOBS]] }),
	screen(NavigationAction.JOB_DETAIL, NavigationCategory.JOBS, '/jobs/{jobId}', { requiredPermissions: [JOBS_READ], requiredCapabilities: [[Capabilities.JOBS]] }),
	screen(NavigationAction.JOB_SUB_JOBS, NavigationCategory.JOBS, '/jobs/{jobId}/sub-jobs', { requiredPermissions: [JOBS_READ], requiredCapabilities: [[Capabilities.JOBS]] }),
	screen(NavigationAction.JOB_QUOTES, NavigationCategory.JOBS, '/jobs/{jobId}/quotes', { requiredPermissions: [JOBS_READ], requiredCapabilities: [[Capabilities.JOBS]] }),
	screen(NavigationAction.JOB_ACTIVITY, NavigationCategory.JOBS, '/jobs/{jobId}/activity', { requiredPermissions: [JOBS_READ], requiredCapabilities: [[Capabilities.JOBS]] }),
	screen(NavigationAction.JOB_ORDERS, NavigationCategory.JOBS, '/jobs/{jobId}/orders', { requiredPermissions: [JOBS_READ], requiredCapabilities: [[Capabilities.JOBS]] }),
	screen(NavigationAction.JOB_INVOICES, NavigationCategory.JOBS, '/jobs/{jobId}/invoices', { requiredPermissions: [JOBS_READ], requiredCapabilities: [[Capabilities.JOBS]] }),
	screen(NavigationAction.JOB_NOTES, NavigationCategory.JOBS, '/jobs/{jobId}/notes', { requiredPermissions: [JOBS_READ], requiredCapabilities: [[Capabilities.JOBS]] }),
	// Campaigns
	screen(NavigationAction.CAMPAIGN_LIST, NavigationCategory.CAMPAIGNS, '/campaigns', { requiredPermissions: [MARKETING_CAMPAIGNS_READ], requiredCapabilities: [[Capabilities.CAMPAIGNS]] }),
	screen(NavigationAction.CAMPAIGN_INBOX, NavigationCategory.CAMPAIGNS, '/campaigns/inbox', { requiredPermissions: [MARKETING_CAMPAIGNS_READ], requiredCapabilities: [[Capabilities.CAMPAIGNS]], hint: 'replies to sales campaigns' }),
	screen(NavigationAction.CAMPAIGN_DETAIL, NavigationCategory.CAMPAIGNS, '/campaigns/{campaignId}', { requiredPermissions: [MARKETING_CAMPAIGNS_READ], requiredCapabilities: [[Capabilities.CAMPAIGNS]] }),
	screen(NavigationAction.CAMPAIGN_BUILD, NavigationCategory.CAMPAIGNS, '/campaigns/{campaignId}/build', { requiredPermissions: [[RbacActions.MARKETING.CAMPAIGNS.UPDATE.action]], requiredCapabilities: [[Capabilities.CAMPAIGNS]] }),
	screen(NavigationAction.MARKETING_CAMPAIGN_LIST, NavigationCategory.CAMPAIGNS, '/marketing/campaigns', { requiredPermissions: [MARKETING_CAMPAIGNS_READ], requiredCapabilities: [[Capabilities.MANAGED_EMAIL], [Capabilities.ECOMMERCE, Capabilities.MARKETING, Capabilities.WEB]] }),
	screen(NavigationAction.MARKETING_CAMPAIGN_DETAIL, NavigationCategory.CAMPAIGNS, '/marketing/campaigns/{marketingCampaignId}', { requiredPermissions: [MARKETING_CAMPAIGNS_READ], requiredCapabilities: [[Capabilities.MANAGED_EMAIL], [Capabilities.ECOMMERCE, Capabilities.MARKETING, Capabilities.WEB]] }),
	screen(NavigationAction.MARKETING_MEDIA, NavigationCategory.CAMPAIGNS, '/marketing/media', { requiredPermissions: [[RbacActions.MEDIA.READ.action]], requiredCapabilities: [[Capabilities.ECOMMERCE, Capabilities.MARKETING, Capabilities.WEB]] }),
	screen(NavigationAction.FORM_LIST, NavigationCategory.CAMPAIGNS, '/forms', { requiredCapabilities: [[Capabilities.ECOMMERCE, Capabilities.MARKETING, Capabilities.WEB]] }),
	screen(NavigationAction.FORM_DETAIL, NavigationCategory.CAMPAIGNS, '/forms/{formId}', { requiredCapabilities: [[Capabilities.ECOMMERCE, Capabilities.MARKETING, Capabilities.WEB]] }),
	screen(NavigationAction.FORMS_SENT, NavigationCategory.CAMPAIGNS, '/forms/sent'),
	screen(NavigationAction.FORM_SUBMISSIONS, NavigationCategory.CAMPAIGNS, '/forms/submissions', { requiredCapabilities: [[Capabilities.ECOMMERCE, Capabilities.MARKETING, Capabilities.WEB]] }),
	screen(NavigationAction.FORM_SUBMISSION_DETAIL, NavigationCategory.CAMPAIGNS, '/forms/submissions/{formSubmissionId}', { requiredCapabilities: [[Capabilities.ECOMMERCE, Capabilities.MARKETING, Capabilities.WEB]] }),
	// Communications
	screen(NavigationAction.INBOX, NavigationCategory.COMMUNICATIONS, '/inbox', { requiredPermissions: [[RbacActions.COMMUNICATIONS.INBOX.VIEW.action]], requiredCapabilities: [[Capabilities.SMS, Capabilities.SOFTPHONE]], hint: 'calls and texts' }),
	screen(NavigationAction.LIVE_CALLS, NavigationCategory.COMMUNICATIONS, '/live-calls', { requiredPermissions: [[RbacActions.PHONE.LIVE_CALLS.VIEW.action]], requiredCapabilities: [[Capabilities.SOFTPHONE]] }),
	screen(NavigationAction.EMAIL_MAILBOX, NavigationCategory.COMMUNICATIONS, '/email/mailbox', { hint: 'the signed-in user\'s mailbox' }),
	screen(NavigationAction.EMAIL_SENT, NavigationCategory.COMMUNICATIONS, '/email/sent'),
	screen(NavigationAction.EMAIL_SENT_DETAIL, NavigationCategory.COMMUNICATIONS, '/email/sent/{emailId}'),
	// Distribution
	screen(NavigationAction.DISTRIBUTION_ROUTES, NavigationCategory.DISTRIBUTION, '/distribution', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.DISTRIBUTION_MAP, NavigationCategory.DISTRIBUTION, '/distribution/map', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.DISTRIBUTION_LIVE_BOARD, NavigationCategory.DISTRIBUTION, '/distribution/live-board', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.DISTRIBUTION_MY_TRIPS, NavigationCategory.DISTRIBUTION, '/distribution/my-trips', { requiredCapabilities: [[Capabilities.DISTRIBUTION, Capabilities.SERVICE]] }),
	screen(NavigationAction.DISTRIBUTION_DISPATCH, NavigationCategory.DISTRIBUTION, '/distribution/dispatch', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.DISTRIBUTION_SCHEDULE, NavigationCategory.DISTRIBUTION, '/distribution/schedule', { requiredPermissions: [[RbacActions.DISTRIBUTION.SCHEDULE.action]], requiredCapabilities: [[Capabilities.DISTRIBUTION, Capabilities.EQUIPMENT, Capabilities.SERVICE]] }),
	screen(NavigationAction.DISTRIBUTION_REPLENISHMENT, NavigationCategory.DISTRIBUTION, '/distribution/replenishment', { requiredPermissions: [[RbacActions.DISTRIBUTION.REPLENISHMENT.action]], requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.DISTRIBUTION_REBALANCE, NavigationCategory.DISTRIBUTION, '/distribution/rebalance', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.DISTRIBUTION_ASSIGN_UNROUTED, NavigationCategory.DISTRIBUTION, '/distribution/assign-unrouted', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.ROUTE_DETAIL, NavigationCategory.DISTRIBUTION, '/distribution/route/{routeId}', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.ROUTE_STOPS, NavigationCategory.DISTRIBUTION, '/distribution/route/{routeId}/stops', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.TRIP_LIST, NavigationCategory.DISTRIBUTION, '/distribution/trips', { requiredCapabilities: [[Capabilities.DISTRIBUTION, Capabilities.SERVICE]] }),
	screen(NavigationAction.TRIP_FULFILLMENT, NavigationCategory.DISTRIBUTION, '/distribution/trips/fulfillment', { requiredPermissions: [[RbacActions.ORDERS.FULFILLMENT.VIEW.action]], requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.TRIP_MAP, NavigationCategory.DISTRIBUTION, '/distribution/trips/map', { requiredCapabilities: [[Capabilities.DISTRIBUTION, Capabilities.SERVICE]] }),
	screen(NavigationAction.TRIP_DETAIL, NavigationCategory.DISTRIBUTION, '/distribution/trip/{tripId}', { requiredCapabilities: [[Capabilities.DISTRIBUTION, Capabilities.SERVICE]] }),
	screen(NavigationAction.TRIP_STOPS, NavigationCategory.DISTRIBUTION, '/distribution/trip/{tripId}/stops', { requiredCapabilities: [[Capabilities.DISTRIBUTION, Capabilities.SERVICE]] }),
	screen(NavigationAction.TRIP_DRIVER, NavigationCategory.DISTRIBUTION, '/distribution/trip/{tripId}/driver', { requiredCapabilities: [[Capabilities.DISTRIBUTION, Capabilities.SERVICE]] }),
	screen(NavigationAction.TRIP_VEHICLE, NavigationCategory.DISTRIBUTION, '/distribution/trip/{tripId}/vehicle', { requiredCapabilities: [[Capabilities.DISTRIBUTION, Capabilities.SERVICE]] }),
	screen(NavigationAction.TRIP_NOTES, NavigationCategory.DISTRIBUTION, '/distribution/trip/{tripId}/notes', { requiredCapabilities: [[Capabilities.DISTRIBUTION, Capabilities.SERVICE]] }),
	screen(NavigationAction.TRIP_PHOTOS, NavigationCategory.DISTRIBUTION, '/distribution/trip/{tripId}/photos', { requiredCapabilities: [[Capabilities.DISTRIBUTION, Capabilities.SERVICE]] }),
	screen(NavigationAction.TRIP_HISTORY, NavigationCategory.DISTRIBUTION, '/distribution/trip/{tripId}/history', { requiredCapabilities: [[Capabilities.DISTRIBUTION, Capabilities.SERVICE]] }),
	screen(NavigationAction.TRIP_INVENTORY, NavigationCategory.DISTRIBUTION, '/distribution/trip/{tripId}/inventory', { requiredCapabilities: [[Capabilities.DISTRIBUTION, Capabilities.SERVICE]] }),
	screen(NavigationAction.DRIVER_LIST, NavigationCategory.DISTRIBUTION, '/distribution/drivers', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.DRIVER_DETAIL, NavigationCategory.DISTRIBUTION, '/distribution/drivers/{driverId}', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.DRIVER_ROUTES, NavigationCategory.DISTRIBUTION, '/distribution/drivers/{driverId}/routes', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.DRIVER_TRIPS, NavigationCategory.DISTRIBUTION, '/distribution/drivers/{driverId}/trips', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.VEHICLE_LIST, NavigationCategory.DISTRIBUTION, '/distribution/vehicles', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.VEHICLE_MAP, NavigationCategory.DISTRIBUTION, '/distribution/vehicles/map', { requiredCapabilities: [[Capabilities.FLEET]] }),
	screen(NavigationAction.VEHICLE_DETAIL, NavigationCategory.DISTRIBUTION, '/distribution/vehicles/{vehicleId}', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.VEHICLE_ROUTES, NavigationCategory.DISTRIBUTION, '/distribution/vehicles/{vehicleId}/routes', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.VEHICLE_TRIPS, NavigationCategory.DISTRIBUTION, '/distribution/vehicles/{vehicleId}/trips', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.VEHICLE_ITEMS, NavigationCategory.DISTRIBUTION, '/distribution/vehicles/{vehicleId}/items', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.VEHICLE_ISSUES, NavigationCategory.DISTRIBUTION, '/distribution/vehicles/{vehicleId}/issues', { requiredCapabilities: [[Capabilities.DISTRIBUTION]] }),
	screen(NavigationAction.MERCHANDISER_LIST, NavigationCategory.DISTRIBUTION, '/distribution/merchandisers', { requiredCapabilities: [[Capabilities.EQUIPMENT]] }),
	screen(NavigationAction.MERCHANDISER_MAP, NavigationCategory.DISTRIBUTION, '/distribution/merchandisers/map', { requiredCapabilities: [[Capabilities.EQUIPMENT]] }),
	screen(NavigationAction.MERCHANDISER_DETAIL, NavigationCategory.DISTRIBUTION, '/distribution/merchandisers/{merchandiserId}', { requiredCapabilities: [[Capabilities.EQUIPMENT]] }),
	screen(NavigationAction.MERCHANDISER_TEMPLATES, NavigationCategory.DISTRIBUTION, '/distribution/merchandiser-templates', { requiredCapabilities: [[Capabilities.EQUIPMENT]] }),
	screen(NavigationAction.SENSOR_DEVICE_LIST, NavigationCategory.DISTRIBUTION, '/distribution/sensor-devices', { requiredCapabilities: [[Capabilities.EQUIPMENT]] }),
	screen(NavigationAction.SENSOR_DEVICE_DETAIL, NavigationCategory.DISTRIBUTION, '/distribution/sensor-devices/{sensorDeviceId}', { requiredCapabilities: [[Capabilities.EQUIPMENT]] }),
	screen(NavigationAction.SENSOR_MODULE_FAULTS, NavigationCategory.DISTRIBUTION, '/distribution/sensor-devices/module-faults', { requiredCapabilities: [[Capabilities.EQUIPMENT]] }),
	// Warehouses
	screen(NavigationAction.WAREHOUSE_LIST, NavigationCategory.WAREHOUSES, '/warehouses'),
	screen(NavigationAction.WAREHOUSE_DETAIL, NavigationCategory.WAREHOUSES, '/warehouses/{warehouseId}'),
	screen(NavigationAction.WAREHOUSE_ITEMS, NavigationCategory.WAREHOUSES, '/warehouses/{warehouseId}/warehouse-items'),
	screen(NavigationAction.WAREHOUSE_LOTS, NavigationCategory.WAREHOUSES, '/warehouses/{warehouseId}/lots'),
	screen(NavigationAction.WAREHOUSE_LOCATIONS, NavigationCategory.WAREHOUSES, '/warehouses/{warehouseId}/locations'),
	screen(NavigationAction.WAREHOUSE_MOVEMENTS, NavigationCategory.WAREHOUSES, '/warehouses/{warehouseId}/movements'),
	screen(NavigationAction.LOCATION_LIST, NavigationCategory.WAREHOUSES, '/locations'),
	screen(NavigationAction.LOCATION_MAP, NavigationCategory.WAREHOUSES, '/locations/map'),
	screen(NavigationAction.LOCATION_DETAIL, NavigationCategory.WAREHOUSES, '/locations/{locationId}'),
	screen(NavigationAction.LOCATION_ITEMS, NavigationCategory.WAREHOUSES, '/locations/{locationId}/items'),
	screen(NavigationAction.LOCATION_LOTS, NavigationCategory.WAREHOUSES, '/locations/{locationId}/lots'),
	screen(NavigationAction.LOCATION_SENSORS, NavigationCategory.WAREHOUSES, '/locations/{locationId}/sensors', { requiredCapabilities: [[Capabilities.EQUIPMENT]] }),
	screen(NavigationAction.LOCATION_SENSOR_DETAIL, NavigationCategory.WAREHOUSES, '/locations/{locationId}/sensors/{sensorId}', { requiredCapabilities: [[Capabilities.EQUIPMENT]] }),
	screen(NavigationAction.LOCATION_SENSOR_HISTORY, NavigationCategory.WAREHOUSES, '/locations/{locationId}/sensors/{sensorId}/historical-data', { requiredCapabilities: [[Capabilities.EQUIPMENT]] }),
	// Sales Routes
	screen(NavigationAction.SALES_ROUTES, NavigationCategory.SALES_ROUTES, '/sales-routes', { requiredPermissions: [[RbacActions.SALES_ROUTES.VIEW_ANY.action, RbacActions.SALES_ROUTES.VIEW_SELF.action, RbacActions.SALES_TRIPS.VIEW_ANY.action, RbacActions.SALES_TRIPS.VIEW_SELF.action]], requiredCapabilities: [[Capabilities.FIELD_SALES]] }),
	screen(NavigationAction.SALES_ROUTE_PLANNER, NavigationCategory.SALES_ROUTES, '/sales-routes/plan', { requiredPermissions: [SALES_TRIPS_VIEW], requiredCapabilities: [[Capabilities.FIELD_SALES]] }),
	screen(NavigationAction.SALES_ROUTE_DETAIL, NavigationCategory.SALES_ROUTES, '/sales-routes/routes/{salesRouteId}', { requiredPermissions: [[RbacActions.SALES_ROUTES.VIEW_ANY.action, RbacActions.SALES_ROUTES.VIEW_SELF.action]], requiredCapabilities: [[Capabilities.FIELD_SALES]] }),
	screen(NavigationAction.SALES_TRIP_DETAIL, NavigationCategory.SALES_ROUTES, '/sales-routes/trips/{salesTripId}', { requiredPermissions: [SALES_TRIPS_VIEW], requiredCapabilities: [[Capabilities.FIELD_SALES]] }),
	screen(NavigationAction.SALES_TRIP_RUN, NavigationCategory.SALES_ROUTES, '/sales-routes/trips/{salesTripId}/run', { requiredPermissions: [SALES_TRIPS_VIEW], requiredCapabilities: [[Capabilities.FIELD_SALES]] }),
	screen(NavigationAction.SALES_PLAN_DETAIL, NavigationCategory.SALES_ROUTES, '/sales-routes/plans/{salesPlanId}', { requiredPermissions: [SALES_TRIPS_VIEW], requiredCapabilities: [[Capabilities.FIELD_SALES]] }),
	// Compliance
	screen(NavigationAction.COMPLIANCE_DASHBOARD, NavigationCategory.COMPLIANCE, '/compliance/dashboard', { requiredPermissions: [LICENSES_VIEW_ANY], requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.COMPLIANCE_ATTENTION, NavigationCategory.COMPLIANCE, '/compliance/attention', { requiredPermissions: [LICENSES_VIEW_ANY], requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.COMPLIANCE_EXCLUSIONS, NavigationCategory.COMPLIANCE, '/compliance/exclusions', { requiredPermissions: [LICENSES_VIEW_ANY], requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.COMPLIANCE_ERP_ACTIVITY, NavigationCategory.COMPLIANCE, '/compliance/erp-activity', { requiredPermissions: [LICENSES_VIEW_ANY], requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.COMPLIANCE_RECENT_CHANGES, NavigationCategory.COMPLIANCE, '/compliance/recent-changes', { requiredPermissions: [LICENSES_VIEW_ANY], requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.LICENSE_REGISTRY, NavigationCategory.COMPLIANCE, '/registry', { requiredPermissions: [LICENSES_VIEW_ANY], requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.LICENSE_LIST, NavigationCategory.COMPLIANCE, '/licenses', { requiredPermissions: [LICENSES_VIEW_ANY], requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.LICENSE_MATCHING, NavigationCategory.COMPLIANCE, '/licenses/matching', { requiredPermissions: [LICENSES_VIEW_ANY], requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.LICENSE_MATCHES, NavigationCategory.COMPLIANCE, '/licenses/matches', { requiredPermissions: [LICENSES_VIEW_ANY], requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.LICENSE_DETAIL, NavigationCategory.COMPLIANCE, '/licenses/{licenseId}', { requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.LICENSE_LINKS, NavigationCategory.COMPLIANCE, '/licenses/{licenseId}/links', { requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.LICENSE_NOTES, NavigationCategory.COMPLIANCE, '/licenses/{licenseId}/notes', { requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.LICENSE_TASKS, NavigationCategory.COMPLIANCE, '/licenses/{licenseId}/tasks', { requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.LICENSE_DISCREPANCIES, NavigationCategory.COMPLIANCE, '/licenses/{licenseId}/discrepancies', { requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.LICENSE_DISCIPLINARY, NavigationCategory.COMPLIANCE, '/licenses/{licenseId}/disciplinary', { requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.LICENSE_AUDIT, NavigationCategory.COMPLIANCE, '/licenses/{licenseId}/audit', { requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	screen(NavigationAction.LICENSE_FILES, NavigationCategory.COMPLIANCE, '/licenses/{licenseId}/files', { requiredCapabilities: [[Capabilities.COMPLIANCE]] }),
	// Automation
	screen(NavigationAction.AUTOMATION_HOME, NavigationCategory.AUTOMATION, '/automation'),
	screen(NavigationAction.AUTOMATION_FLOWS, NavigationCategory.AUTOMATION, '/automation/flows'),
	screen(NavigationAction.AUTOMATION_FLOW_DETAIL, NavigationCategory.AUTOMATION, '/automation/flows/{flowId}'),
	screen(NavigationAction.AUTOMATION_TRIGGERS, NavigationCategory.AUTOMATION, '/automation/triggers'),
	screen(NavigationAction.AUTOMATION_TRIGGER_DETAIL, NavigationCategory.AUTOMATION, '/automation/triggers/{triggerId}'),
	screen(NavigationAction.AUTOMATION_CAMPAIGNS, NavigationCategory.AUTOMATION, '/automation/campaigns'),
	screen(NavigationAction.AUTOMATION_CAMPAIGN_DETAIL, NavigationCategory.AUTOMATION, '/automation/campaigns/{automationCampaignId}'),
	screen(NavigationAction.AUTOMATION_TARGET_LISTS, NavigationCategory.AUTOMATION, '/automation/target-lists'),
	screen(NavigationAction.AUTOMATION_TARGET_LIST_DETAIL, NavigationCategory.AUTOMATION, '/automation/target-lists/{targetListId}'),
	screen(NavigationAction.AUTOMATION_EXECUTIONS, NavigationCategory.AUTOMATION, '/automation/executions'),
	// Ai Agents
	screen(NavigationAction.AI_AGENTS_HOME, NavigationCategory.AI_AGENTS, '/ai-agents', { requiredCapabilities: [[Capabilities.AI_AGENTS]] }),
	screen(NavigationAction.AI_AGENT_LIST, NavigationCategory.AI_AGENTS, '/ai-agents/agents', { requiredCapabilities: [[Capabilities.AI_AGENTS]] }),
	screen(NavigationAction.AI_AGENT_DETAIL, NavigationCategory.AI_AGENTS, '/ai-agents/agents/{aiAgentId}', { requiredCapabilities: [[Capabilities.AI_AGENTS]] }),
	screen(NavigationAction.AI_CONVERSATION_LIST, NavigationCategory.AI_AGENTS, '/ai-agents/conversations', { requiredCapabilities: [[Capabilities.AI_AGENTS]] }),
	screen(NavigationAction.AI_CONVERSATION_DETAIL, NavigationCategory.AI_AGENTS, '/ai-agents/conversations/{conversationId}', { requiredCapabilities: [[Capabilities.AI_AGENTS]] }),
	screen(NavigationAction.AI_ANALYTICS, NavigationCategory.AI_AGENTS, '/ai-agents/analytics', { requiredCapabilities: [[Capabilities.AI_AGENTS]] }),
	// Reports
	screen(NavigationAction.REPORTS_DASHBOARD, NavigationCategory.REPORTS, '/reporting'),
	screen(NavigationAction.REPORT_LIST, NavigationCategory.REPORTS, '/reporting/reports'),
	screen(NavigationAction.REPORT_CATEGORY, NavigationCategory.REPORTS, '/reporting/categories/{category}', { pages: NAVIGATION_REPORT_CATEGORY_PAGES }),
	screen(NavigationAction.REPORT_PAGE, NavigationCategory.REPORTS, '/reporting/reports/{report}', { pages: NAVIGATION_REPORT_PAGES }),
	// Settings
	screen(NavigationAction.SETTINGS, NavigationCategory.SETTINGS, '/settings'),
	screen(NavigationAction.SETTINGS_PAGE, NavigationCategory.SETTINGS, '/settings/{page}', { pages: NAVIGATION_SETTINGS_PAGES }),
	screen(NavigationAction.GROUP_DETAIL, NavigationCategory.SETTINGS, '/settings/groups/{groupId}', { requiredPermissions: [[RbacActions.GROUPS.READ.action]] }),
	screen(NavigationAction.TEAM_DETAIL, NavigationCategory.SETTINGS, '/settings/teams/{teamId}', { requiredPermissions: [TEAMS_READ] }),
	screen(NavigationAction.BRAND_DETAIL, NavigationCategory.SETTINGS, '/settings/brands/{brandId}', { requiredPermissions: [[RbacActions.SETTINGS.BRANDS_STOREFRONTS.READ.action]], requiredCapabilities: [[Capabilities.ECOMMERCE, Capabilities.WEB]] }),
	screen(NavigationAction.STOREFRONT_USER_DETAIL, NavigationCategory.SETTINGS, '/settings/storefront-users/{storefrontUserId}', { requiredPermissions: [[RbacActions.ECOMMERCE_USERS.READ.action]], requiredCapabilities: [[Capabilities.ECOMMERCE]] }),
];

/**
 * Whether the route is open to this tenant and user. With capabilities unknown (null) a
 * capability-gated route is left out: fail closed, as the tool advertisement gate does.
 */
export function isNavigationRouteAvailable(
	route: NavigationRouteDefinition,
	capabilities: ReadonlySet<string> | null,
	permissions: ReadonlySet<string>,
): boolean {
	return (
		guardsHold(route, capabilities, permissions) &&
		(route.pages == null || route.pages.some((page) => guardsHold(page, capabilities, permissions)))
	);
}

/** The params a route template names: `{clientId}` is a number, anything not ending in Id a word. */
function templateParams(routeTemplate: string): Array<NavigationParamDefinition> {
	return [...routeTemplate.matchAll(/\{(\w+)\}/g)].map(([, name = '']) => ({
		name,
		type: name.endsWith('Id') ? 'number' : 'string',
		description: `The ${name.replace(/Id$/, ' ID').replace(/[A-Z]/g, (letter) => ` ${letter.toLowerCase()}`)}`,
	}));
}

/** Whether these guards hold for this tenant and user. */
function guardsHold(
	guards: NavigationGuardsModel,
	capabilities: ReadonlySet<string> | null,
	permissions: ReadonlySet<string>,
): boolean {
	return (
		(guards.requiredPermissions ?? []).every((list) => list.some((action) => permissions.has(action))) &&
		(guards.requiredCapabilities ?? []).every(
			(list) => capabilities != null && list.some((capability) => capabilities.has(capability)),
		)
	);
}

/**
 * The routes this tenant and user can open. A route of many pages keeps only the pages
 * that are open to them, and drops out when none is.
 */
export function getAvailableNavigationRoutes(
	capabilities: ReadonlySet<string> | null,
	permissions: ReadonlySet<string>,
): Array<NavigationRouteDefinition> {
	return NavigationRoutes.filter((route) => isNavigationRouteAvailable(route, capabilities, permissions)).map(
		(route) =>
			route.pages == null
				? route
				: { ...route, pages: route.pages.filter((page) => guardsHold(page, capabilities, permissions)) },
	);
}

/**
 * Get a navigation route by action
 */
export function getNavigationRouteByAction(
	action: NavigationAction,
): NavigationRouteDefinition | undefined {
	return NavigationRoutes.find((route) => route.action === action);
}

/**
 * The actions as they go into the tool description. It is sent on every turn, so the
 * action names carry the meaning and actions with the same params share a line: a
 * sentence per screen would add thousands of tokens to each call.
 */
export function getNavigationActionsDescription(
	routes: ReadonlyArray<NavigationRouteDefinition> = NavigationRoutes,
): string {
	const categories = new Map<NavigationCategory, Map<string, Array<string>>>();
	const lines: Array<string> = [];

	for (const route of routes) {
		const byParams = categories.get(route.category) ?? new Map<string, Array<string>>();
		const params = route.requiredParams.map((param) => param.name).join(', ');
		const name = route.hint != null ? `${route.action} (${route.hint})` : route.action;

		categories.set(route.category, byParams);

		if (route.pages != null) {
			byParams.set(route.action, [`${name} with ${params} one of: ${route.pages.map((page) => page.value).join(', ')}`]);
		} else {
			byParams.set(params, [...(byParams.get(params) ?? []), name]);
		}
	}

	for (const [category, byParams] of categories) {
		lines.push(`\n${category.toUpperCase()}:`);

		for (const [params, entries] of byParams) {
			lines.push(`  ${params === '' || entries[0]?.startsWith(params) === true ? '' : `needs ${params}: `}${entries.join(', ')}`);
		}
	}

	return lines.join('\n');
}

/**
 * Resolve a route template with parameters
 */
export function resolveNavigationRoute(
	routeTemplate: string,
	params: Record<string, string | number>,
): string {
	let resolvedRoute = routeTemplate;
	for (const [key, value] of Object.entries(params)) {
		resolvedRoute = resolvedRoute.replace(`{${key}}`, String(value));
	}
	return resolvedRoute;
}

/**
 * Validate that all required parameters are provided
 */
export function validateNavigationParams(
	route: NavigationRouteDefinition,
	params: Record<string, unknown>,
): { valid: boolean; missingParams: Array<NavigationParamDefinition> } {
	const missingParams: Array<NavigationParamDefinition> = [];

	for (const requiredParam of route.requiredParams) {
		const value = params[requiredParam.name];

		if (
			value == null ||
			(requiredParam.type === 'number' && !Number.isSafeInteger(Number(value))) ||
			(route.pages != null && !route.pages.some((page) => page.value === value))
		) {
			missingParams.push(requiredParam);
		}
	}

	return {
		valid: missingParams.length === 0,
		missingParams,
	};
}
